import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import ReactSelect from 'react-select';
import { Card, CardBody, Typography, Input, Button } from '@material-tailwind/react';
import { FaBoxOpen, FaFilter, FaSyncAlt, FaSearch } from 'react-icons/fa';
import { rootUrl } from '../../../config/config';
import { formatCurrency } from './shared/reportUtils';
import ReportLink from './shared/ReportLink';

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '40px',
    borderRadius: '0.75rem',
    borderColor: state.isFocused ? '#FF5733' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(255,87,51,0.15)' : 'none',
    '&:hover': { borderColor: '#FF5733' },
    fontSize: '0.875rem',
  }),
  menu: (base) => ({ ...base, zIndex: 50, borderRadius: '0.75rem', fontSize: '0.875rem' }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? '#fff7ed' : state.isSelected ? '#ffedd5' : '#fff',
    color: '#1f2937',
  }),
};

const EmptyState = ({ title, message }) => (
  <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center">
    <Typography className="text-lg font-semibold text-slate-700">{title}</Typography>
    <p className="mt-2 text-sm text-slate-500">{message}</p>
  </div>
);

const StatCard = ({ title, value, hint }) => (
  <Card className="border border-[#eadfd8] shadow-sm rounded-2xl bg-white">
    <CardBody className="p-4">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 truncate">{title}</p>
      <div className="mt-1.5 text-lg font-black leading-tight break-words text-slate-900">{value}</div>
      {hint ? <p className="mt-1 text-xs text-slate-500 truncate">{hint}</p> : null}
    </CardBody>
  </Card>
);

const StockValuationReport = () => {
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [draftFilters, setDraftFilters] = useState({
    category: '',
    manufacturer: '',
    status: '',
    search: '',
    low_stock_only: false
  });
  const [appliedFilters, setAppliedFilters] = useState(draftFilters);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadMeta = async () => {
      setLoadingMeta(true);
      try {
        const token = localStorage.getItem('authToken') || 'default-token';
        const headers = { Authorization: `Bearer ${token}` };
        const [categoryRes, manufacturerRes, statusRes] = await Promise.all([
          axios.get(`${rootUrl}/parts/categories`, { headers }),
          axios.get(`${rootUrl}/parts/manufacturers`, { headers }),
          axios.get(`${rootUrl}/parts/statuses`, { headers })
        ]);
        setCategories(Array.isArray(categoryRes.data) ? categoryRes.data : []);
        setManufacturers(Array.isArray(manufacturerRes.data) ? manufacturerRes.data : []);
        setStatuses(Array.isArray(statusRes.data) ? statusRes.data : []);
      } catch (err) {
        console.error('Failed to load valuation filters:', err);
      } finally {
        setLoadingMeta(false);
      }
    };
    loadMeta();
  }, []);

  useEffect(() => {
    const loadReport = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('authToken') || 'default-token';
        const params = new URLSearchParams();
        if (appliedFilters.category) params.append('category', appliedFilters.category);
        if (appliedFilters.manufacturer) params.append('manufacturer', appliedFilters.manufacturer);
        if (appliedFilters.status) params.append('status', appliedFilters.status);
        if (appliedFilters.search.trim()) params.append('search', appliedFilters.search.trim());
        if (appliedFilters.low_stock_only) params.append('low_stock_only', 'true');

        const response = await axios.get(`${rootUrl}/api/reports/stock-valuation?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setReport(response.data?.data || response.data || null);
      } catch (err) {
        console.error('Failed to load stock valuation report:', err);
        setError(err?.response?.data?.message || err.message || 'Failed to load stock valuation report');
      } finally {
        setLoading(false);
      }
    };
    loadReport();
  }, [appliedFilters]);

  const rows = Array.isArray(report?.rows) ? report.rows : [];
  const summary = report?.summary || { item_count: 0, total_quantity: 0, stock_value: 0, selling_value: 0, low_stock_count: 0, stale_pricing_count: 0 };
  const potentialProfit = useMemo(() => summary.selling_value - summary.stock_value, [summary.selling_value, summary.stock_value]);

  const categoryOptions = useMemo(() => categories.map((item) => ({ value: item.name, label: item.name })), [categories]);
  const manufacturerOptions = useMemo(() => manufacturers.map((item) => ({ value: item.name, label: item.name })), [manufacturers]);
  const statusOptions = useMemo(() => statuses.map((item) => ({ value: item.name, label: item.name })), [statuses]);

  const applyFilters = () => setAppliedFilters(draftFilters);
  const resetFilters = () => {
    const next = { category: '', manufacturer: '', status: '', search: '', low_stock_only: false };
    setDraftFilters(next);
    setAppliedFilters(next);
  };

  return (
    <div className="w-full max-w-7xl mx-auto mt-6 px-2 sm:px-4">
      <Card className="border-t">
        <CardBody className="p-3 sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
            <div>
              <Typography variant="h5" className="text-gray-900">Stock Valuation</Typography>
              <Typography className="text-sm text-gray-600">Current inventory value, selling value, and low-stock focus.</Typography>
            </div>
            <div className="flex gap-2">
              <Button onClick={applyFilters} className="bg-brand-primary text-white shadow-none rounded-none flex items-center gap-2">
                <FaFilter /> Apply Filters
              </Button>
              <Button onClick={resetFilters} variant="outlined" color="gray" className="shadow-none rounded-none flex items-center gap-2">
                <FaSyncAlt /> Reset
              </Button>
            </div>
          </div>

          {error && <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700">{error}</div>}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 mb-5">
            <StatCard title="Items" value={summary.item_count || 0} hint="Filtered parts" />
            <StatCard title="Quantity" value={summary.total_quantity || 0} hint="Total units on hand" />
            <StatCard title="Stock Value" value={formatCurrency(summary.stock_value)} hint="Cost-based valuation" />
            <StatCard title="Selling Value" value={formatCurrency(summary.selling_value)} hint="Retail valuation" />
            <StatCard title="Potential Profit" value={formatCurrency(potentialProfit)} hint="Selling minus cost" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 mb-5">
            <div>
              <label className="mb-1 block text-sm text-gray-700">Search</label>
              <div className="flex items-center gap-2">
                <FaSearch className="text-gray-500 shrink-0" />
                <Input
                  placeholder="Part name, number, or ID"
                  value={draftFilters.search}
                  onChange={(e) => setDraftFilters(prev => ({ ...prev, search: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-700">Category</label>
              <ReactSelect
                isClearable
                isLoading={loadingMeta}
                styles={selectStyles}
                options={categoryOptions}
                value={categoryOptions.find((item) => item.value === draftFilters.category) || null}
                onChange={(option) => setDraftFilters(prev => ({ ...prev, category: option?.value || '' }))}
                placeholder="All categories"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-700">Manufacturer</label>
              <ReactSelect
                isClearable
                isLoading={loadingMeta}
                styles={selectStyles}
                options={manufacturerOptions}
                value={manufacturerOptions.find((item) => item.value === draftFilters.manufacturer) || null}
                onChange={(option) => setDraftFilters(prev => ({ ...prev, manufacturer: option?.value || '' }))}
                placeholder="All manufacturers"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-700">Status</label>
              <ReactSelect
                isClearable
                isLoading={loadingMeta}
                styles={selectStyles}
                options={statusOptions}
                value={statusOptions.find((item) => item.value === draftFilters.status) || null}
                onChange={(option) => setDraftFilters(prev => ({ ...prev, status: option?.value || '' }))}
                placeholder="All statuses"
              />
            </div>
            <div className="flex items-end">
              <button
                type="button"
                onClick={() => setDraftFilters(prev => ({ ...prev, low_stock_only: !prev.low_stock_only }))}
                className={`w-full rounded-xl border px-4 py-2 text-sm font-semibold shadow-sm transition ${
                  draftFilters.low_stock_only
                    ? 'border-amber-300 bg-amber-50 text-amber-800'
                    : 'border-slate-300 bg-white text-slate-700'
                }`}
              >
                {draftFilters.low_stock_only ? 'Low stock only' : 'Show low stock only'}
              </button>
            </div>
          </div>

          {loading ? (
            <Typography>Loading stock valuation...</Typography>
          ) : rows.length === 0 ? (
            <EmptyState title="No stock rows" message="Adjust the filters and apply again." />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Part</th>
                    <th className="hidden md:table-cell px-3 py-2">Category</th>
                    <th className="hidden md:table-cell px-3 py-2">Manufacturer</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="hidden lg:table-cell px-3 py-2 text-right">Unit Cost</th>
                    <th className="px-3 py-2 text-right">Stock Value</th>
                    <th className="hidden lg:table-cell px-3 py-2 text-right">Selling Value</th>
                    <th className="hidden xl:table-cell px-3 py-2 text-right">Margin / Markup</th>
                    <th className="px-3 py-2 text-right">Status</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.part_id} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="px-3 py-3 font-semibold text-slate-700">
                        <div className="flex flex-col gap-1">
                          <span>{row.part_name}</span>
                          <div className="flex flex-wrap gap-1">
                            {row.is_stale_pricing ? <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">Stale pricing</span> : null}
                            {row.is_below_min_quantity ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">Below min</span> : null}
                          </div>
                        </div>
                      </td>
                      <td className="hidden md:table-cell px-3 py-3 text-slate-600">{row.category || '-'}</td>
                      <td className="hidden md:table-cell px-3 py-3 text-slate-600">{row.manufacturer || '-'}</td>
                      <td className="px-3 py-3 text-right text-slate-700">{row.quantity}</td>
                      <td className="hidden lg:table-cell px-3 py-3 text-right text-slate-700">{formatCurrency(row.unit_cost)}</td>
                      <td className="px-3 py-3 text-right text-slate-700">{formatCurrency(row.stock_value)}</td>
                      <td className="hidden lg:table-cell px-3 py-3 text-right text-slate-700">{formatCurrency(row.selling_value)}</td>
                      <td className="hidden xl:table-cell px-3 py-3 text-right text-slate-700">
                        {Number(row.calculated_margin_percent || row.margin_percent || 0).toFixed(2)}% / {Number(row.calculated_markup_percent || row.markup_percent || 0).toFixed(2)}%
                      </td>
                      <td className="px-3 py-3 text-right">
                        <span className={`px-2 py-1 rounded-none text-xs font-medium ${(row.status || '').toUpperCase() === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {row.status || 'UNKNOWN'}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-2">
                          <ReportLink to={`/parts/stock-cards/${encodeURIComponent(row.part_id)}`}>Stock Card</ReportLink>
                          <ReportLink to={`/car_parts/${encodeURIComponent(row.part_id)}/price-history`}>Price History</ReportLink>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default StockValuationReport;
